import * as React from "react"
import { navigate } from "gatsby"
import {
  Spacer,
  Link,
  Text,
  Card,
  CardSection,
  CardHeader,
} from "gatsby-interface"
import { SettingsCardEditButton } from "@modules/site/settings"
import { isTrialingPlan, normalizePlan } from "@modules/billing/shared/utils"
import { PlanFeatures } from "./PlanFeatures"

import { planInformation as planText } from "@modules/locales/default.json"
import { OrganizationPermissions } from "@modules/organization/permissions"
import BillingStatus from "./BillingStatus"

import { getPathToOrgSettings } from "@modules/organization/shared/utils"
import {
  OrgSettingsSection,
  OrgSettingsSectionLabel,
} from "@modules/organization/Settings/constants.js"
import PlanSnippet from "@modules/billing/shared/components/PlanSnippet"
import { useOrganizationPlanFieldsFragment } from "@modules/billing/shared/fragments.generated"
import { HelpLink } from "@modules/ui/components/HelpLink"
import { FormattedMessage } from "@modules/locales"
import { supportedPlans } from "@modules/billing/constants"
import { useFlags } from "@modules/featureFlags"

export type PlanInformationProps = {
  organizationId: string
  onBillingPage?: boolean
  className?: string
}

export function PlanInformation({
  organizationId,
  onBillingPage,
  className,
}: PlanInformationProps) {
  const { flags } = useFlags()
  const { data } = useOrganizationPlanFieldsFragment(organizationId)

  if (data === undefined) {
    navigate(getPathToOrgSettings(organizationId))
    return null
  }

  const { billing, rteSummary } = data
  const normalizedPlan = billing?.plan
    ? normalizePlan(billing.plan)
    : billing?.plan
  const billingStatus = billing?.status

  return (
    <Card
      id={OrgSettingsSection.Plan}
      data-cy="plan-information"
      className={className}
    >
      <CardHeader
        title={
          onBillingPage ? (
            planText.headers.currentPlan
          ) : (
            <React.Fragment>
              {OrgSettingsSectionLabel[OrgSettingsSection.Plan]}
              {` `}
              <HelpLink
                href={`${window.location.origin}/pricing/`}
                aria-label={planText.labels.linkToPricingDocs}
                css={theme => ({ marginLeft: theme.space[3] })}
              />
            </React.Fragment>
          )
        }
      >
        {!onBillingPage && (
          <OrganizationPermissions
            id={organizationId}
            resource="billing"
            action="read"
          >
            <SettingsCardEditButton
              onClick={() => {
                navigate(`/dashboard/pricing/${organizationId}`)
              }}
              variant={"GHOST"}
              label={planText.actions.changePlan}
            />
          </OrganizationPermissions>
        )}
      </CardHeader>
      <CardSection applyPaddingTop={false}>
        <PlanSnippet rteSummary={rteSummary} billing={billing} />
      </CardSection>

      <BillingStatus
        orgId={organizationId}
        billing={billing}
        styling={`section`}
        showLink={false}
        uiSource="Organization settings"
      />
      {billingStatus && isTrialingPlan(billingStatus) && (
        <CardSection>
          <PlanFeatures list={normalizedPlan?.features || []} />
          {flags.multiTiersPlans && <Spacer size={5} />}
          {onBillingPage ? (
            <Text
              size="S"
              css={theme => [
                flags.multiTiersPlans && {
                  marginTop: `-${theme.space[8]}`,
                  marginBottom: 0,
                },
              ]}
            >
              <FormattedMessage<"freePlanName">
                message={planText.messages.changePlanTrialEndInformation}
                values={{
                  freePlanName: supportedPlans.Free.name,
                }}
              />
            </Text>
          ) : (
            <Text
              size="S"
              css={theme => [
                flags.multiTiersPlans && {
                  marginBottom: 0,
                  marginTop: `-${theme.space[9]}`,
                },
              ]}
            >
              <FormattedMessage<"trialPlanName" | "freePlanName", "link">
                message={planText.messages.settingsTrialEndInformation}
                values={{
                  trialPlanName: normalizedPlan?.name || "",
                  freePlanName: supportedPlans.Free.name,
                }}
                tags={{
                  link: function(content) {
                    return (
                      <Link
                        variant="SIMPLE"
                        to={`/dashboard/pricing/${organizationId}`}
                      >
                        {content}
                      </Link>
                    )
                  },
                }}
              />
            </Text>
          )}
        </CardSection>
      )}
    </Card>
  )
}
